/**
 * Client-Side Socket Manager
 * 
 * Manages socket connections, cleanup, and prevents memory leaks on the client side.
 * Provides centralized socket lifecycle management with proper cleanup.
 */

import io from "socket.io-client";

const SOCKET_DOMAINS = Object.freeze({
  CHAT: 'chat',
  NOTIFICATION: 'notification',
  SUPPORT: 'support',
});

class ClientSocketManager {
  constructor() {
    this.activeConnections = new Map(); // connectionId -> socket info
    this.reconnectionAttempts = new Map(); // connectionId -> attempts
    this.maxReconnectionAttempts = 5;
    this.reconnectionDelay = 2000;
    this.heartbeatInterval = 30000; // 30 seconds
    this.heartbeatTimeouts = new Map(); // connectionId -> timeout
    this.cleanupCallbacks = new Set(); // Global cleanup callbacks
    this._domainOwners = new Map(); // domain -> { owner, connectionId }
  }

  claimDomain(domain, ownerComponent, connectionId) {
    const existing = this._domainOwners.get(domain);
    if (existing && existing.owner !== ownerComponent) {
      const msg = `[SOCKET GUARD] Domain "${domain}" already owned by "${existing.owner}", but "${ownerComponent}" is trying to claim it. This causes duplicate connections.`;
      if (process.env.NODE_ENV === 'development') {
        throw new Error(msg);
      }
      console.error(msg);
    }
    this._domainOwners.set(domain, { owner: ownerComponent, connectionId });
  }

  releaseDomain(domain, ownerComponent) {
    const existing = this._domainOwners.get(domain);
    if (existing && existing.owner === ownerComponent) {
      this._domainOwners.delete(domain);
    }
  }

  getDomainStats() {
    const stats = {};
    this._domainOwners.forEach((val, key) => { stats[key] = val.owner; });
    return stats;
  }

  /**
   * Create a new socket connection
   */
  createConnection(url, token, room = null, options = {}) {
    const connectionId = this.generateConnectionId(url, token, room);

    // Check if connection already exists
    if (this.activeConnections.has(connectionId)) {
      const existingConnection = this.activeConnections.get(connectionId);

      // If it exists and is truly connected/active, return it
      if (existingConnection.socket && existingConnection.socket.connected && existingConnection.isActive) {
        console.warn(`Connection ${connectionId} already exists and is active`);
        return existingConnection.socket;
      }

      // Otherwise, it's a stale/dead entry. Cleanup so we can create a fresh one.
      console.log(`Connection ${connectionId} exists but is inactive/stale. Cleaning up before recreating.`);
      this.cleanupConnection(connectionId);
    }

    const connectionOptions = {
      "force new connection": true,
      reconnection: true,
      reconnectionDelay: this.reconnectionDelay,
      reconnectionDelayMax: 60000,
      reconnectionAttempts: this.maxReconnectionAttempts,
      timeout: 10000,
      transports: ["websocket"], // PRODUCTION FIX: Avoid HTTP polling in multi-node environments without sticky sessions
      // PRODUCTION-GRADE: Pass token in auth object instead of query string
      // This is more secure and aligns with Socket.IO best practices
      auth: token ? { token } : undefined,
      // Keep query string for backward compatibility during migration
      query: token ? { access_token: token } : undefined,
      ...options,
    };

    // PRODUCTION-GRADE: Use clean URL without token in query string
    const hostUrl = url;
    const socket = io(hostUrl, connectionOptions);

    const connectionInfo = {
      connectionId,
      socket,
      url,
      token,
      room,
      connectedAt: new Date(),
      lastActivity: new Date(),
      isActive: true,
      eventListeners: new Map(), // Track event listeners for cleanup
    };

    this.activeConnections.set(connectionId, connectionInfo);
    this.setupConnectionHandlers(connectionInfo);

    return socket;
  }

  /**
   * Setup connection event handlers
   */
  setupConnectionHandlers(connectionInfo) {
    const { socket, connectionId } = connectionInfo;
    const isProd = process.env.NODE_ENV === 'production';

    // Connection event handlers
    const connectHandler = () => {
      if (!isProd) console.log(`Socket ${connectionId} connected`);
      connectionInfo.lastActivity = new Date();
      this.startHeartbeat(connectionId);
    };

    const disconnectHandler = (reason) => {
      if (!isProd) console.log(`Socket ${connectionId} disconnected:`, reason);
      this.stopHeartbeat(connectionId);
      this.handleReconnection(connectionId, reason);
    };

    const errorHandler = (error) => {
      // In production, suppress noisy WebSocket connection errors
      if (!isProd) console.error(`Socket ${connectionId} error:`, error);
      this.stopHeartbeat(connectionId);
    };

    const reconnectHandler = (attemptNumber) => {
      if (!isProd) console.log(`Socket ${connectionId} reconnecting (attempt ${attemptNumber})`);
      connectionInfo.lastActivity = new Date();
    };

    const reconnectFailedHandler = () => {
      if (!isProd) console.error(`Socket ${connectionId} reconnection failed`);
      this.cleanupConnection(connectionId);
    };

    // Store handlers for cleanup
    connectionInfo.eventListeners.set('connect', connectHandler);
    connectionInfo.eventListeners.set('disconnect', disconnectHandler);
    connectionInfo.eventListeners.set('error', errorHandler);
    connectionInfo.eventListeners.set('reconnect', reconnectHandler);
    connectionInfo.eventListeners.set('reconnect_failed', reconnectFailedHandler);

    // Add event listeners
    socket.on('connect', connectHandler);
    socket.on('disconnect', disconnectHandler);
    socket.on('error', errorHandler);
    socket.on('reconnect', reconnectHandler);
    socket.on('reconnect_failed', reconnectFailedHandler);
  }

  /**
   * Handle reconnection logic
   */
  handleReconnection(connectionId, reason) {
    const connectionInfo = this.activeConnections.get(connectionId);
    if (!connectionInfo) return;

    const attempts = this.reconnectionAttempts.get(connectionId) || 0;

    if (attempts < this.maxReconnectionAttempts) {
      this.reconnectionAttempts.set(connectionId, attempts + 1);

      // Exponential backoff
      const delay = this.reconnectionDelay * Math.pow(2, attempts);
      setTimeout(() => {
        if (connectionInfo.isActive) {
          connectionInfo.socket.connect();
        }
      }, delay);
    } else {
      console.error(`Max reconnection attempts reached for ${connectionId}`);
      this.cleanupConnection(connectionId);
    }
  }

  /**
   * Start heartbeat monitoring
   */
  startHeartbeat(connectionId) {
    this.stopHeartbeat(connectionId); // Clear any existing heartbeat

    const heartbeat = setInterval(() => {
      const connectionInfo = this.activeConnections.get(connectionId);
      if (!connectionInfo || !connectionInfo.isActive) {
        this.stopHeartbeat(connectionId);
        return;
      }

      // Check for inactivity (30 minutes - for notification system, we need long-lived connections)
      const inactivityThreshold = 30 * 60 * 1000; // 30 minutes
      const timeSinceActivity = Date.now() - connectionInfo.lastActivity.getTime();

      // Don't disconnect on inactivity - just log it
      // Socket.io built-in ping/pong will keep connection alive
      if (timeSinceActivity > inactivityThreshold) {
        console.log(`Socket ${connectionId} has been inactive for ${Math.round(timeSinceActivity / 60000)} minutes`);
        // Don't disconnect - let the socket stay alive for real-time notifications
      }

      // Send ping to keep connection alive and update activity
      if (connectionInfo.socket.connected) {
        connectionInfo.socket.emit('ping');
      }
    }, this.heartbeatInterval);

    this.heartbeatTimeouts.set(connectionId, heartbeat);
  }

  /**
   * Stop heartbeat monitoring
   */
  stopHeartbeat(connectionId) {
    const heartbeat = this.heartbeatTimeouts.get(connectionId);
    if (heartbeat) {
      clearInterval(heartbeat);
      this.heartbeatTimeouts.delete(connectionId);
    }
  }

  /**
   * Subscribe to an event with automatic cleanup tracking
   */
  subscribeToEvent(connectionId, event, callback) {
    const connectionInfo = this.activeConnections.get(connectionId);
    if (!connectionInfo) {
      console.error(`Connection ${connectionId} not found`);
      return;
    }

    const wrappedCallback = (data) => {
      connectionInfo.lastActivity = new Date();
      callback(data);
    };

    // Store listener for cleanup
    // Ensure eventListeners is a Map (safety check)
    if (!(connectionInfo.eventListeners instanceof Map)) {
      connectionInfo.eventListeners = new Map();
    }

    if (!connectionInfo.eventListeners.has(event)) {
      connectionInfo.eventListeners.set(event, new Set());
    }

    const eventSet = connectionInfo.eventListeners.get(event);
    if (eventSet && eventSet.add) {
      eventSet.add(wrappedCallback);
    }

    // Add event listener
    connectionInfo.socket.on(event, wrappedCallback);

    return wrappedCallback;
  }

  /**
   * Remove event listener
   */
  removeEventListener(connectionId, event, callback) {
    const connectionInfo = this.activeConnections.get(connectionId);
    if (!connectionInfo) return;

    const listeners = connectionInfo.eventListeners.get(event);
    if (listeners && listeners.has(callback)) {
      connectionInfo.socket.off(event, callback);
      listeners.delete(callback);
    }
  }

  /**
   * Send event with activity tracking
   */
  sendEvent(connectionId, event, data) {
    const connectionInfo = this.activeConnections.get(connectionId);
    if (!connectionInfo || !connectionInfo.isActive) {
      console.error(`Connection ${connectionId} not active`);
      return false;
    }

    connectionInfo.lastActivity = new Date();
    connectionInfo.socket.emit(event, data);
    return true;
  }

  /**
   * Disconnect a specific connection
   */
  disconnectConnection(connectionId) {
    const connectionInfo = this.activeConnections.get(connectionId);
    if (!connectionInfo) return;

    console.log(`Disconnecting connection: ${connectionId}`);

    connectionInfo.isActive = false;
    this.stopHeartbeat(connectionId);
    this.reconnectionAttempts.delete(connectionId);

    if (connectionInfo.socket && connectionInfo.socket.connected) {
      connectionInfo.socket.disconnect();
    }
  }

  /**
   * Cleanup a connection completely
   */
  cleanupConnection(connectionId) {
    const connectionInfo = this.activeConnections.get(connectionId);
    if (!connectionInfo) return;

    console.log(`Cleaning up connection: ${connectionId}`);

    // Stop heartbeat
    this.stopHeartbeat(connectionId);

    // Remove all event listeners
    connectionInfo.eventListeners.forEach((listeners, event) => {
      if (listeners instanceof Set) {
        listeners.forEach(callback => {
          connectionInfo.socket.off(event, callback);
        });
      } else {
        connectionInfo.socket.off(event, listeners);
      }
    });

    // Disconnect socket
    if (connectionInfo.socket) {
      connectionInfo.socket.disconnect();
    }

    // Remove from tracking
    this.activeConnections.delete(connectionId);
    this.reconnectionAttempts.delete(connectionId);
  }

  /**
   * Cleanup all connections
   */
  cleanupAllConnections() {
    console.log('Cleaning up all socket connections...');

    const connectionIds = Array.from(this.activeConnections.keys());
    connectionIds.forEach(connectionId => {
      this.cleanupConnection(connectionId);
    });

    // Clear all timeouts
    this.heartbeatTimeouts.forEach(timeout => {
      clearInterval(timeout);
    });
    this.heartbeatTimeouts.clear();

    // Call global cleanup callbacks
    this.cleanupCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in cleanup callback:', error);
      }
    });
    this.cleanupCallbacks.clear();

    console.log('All socket connections cleaned up');
  }

  /**
   * Add global cleanup callback
   */
  addCleanupCallback(callback) {
    this.cleanupCallbacks.add(callback);
  }

  /**
   * Remove global cleanup callback
   */
  removeCleanupCallback(callback) {
    this.cleanupCallbacks.delete(callback);
  }

  /**
   * Get connection statistics
   */
  getStats() {
    return {
      activeConnections: this.activeConnections.size,
      reconnectionAttempts: this.reconnectionAttempts.size,
      activeHeartbeats: this.heartbeatTimeouts.size,
      cleanupCallbacks: this.cleanupCallbacks.size,
    };
  }

  /**
   * Generate unique connection ID
   */
  generateConnectionId(url, token, room) {
    // Handle null/undefined token
    const tokenId = token ? token.substring(0, 10) : 'no-token';
    return `${url}_${tokenId}_${room || 'default'}`;
  }

  /**
   * Check if connection is active
   */
  isConnectionActive(connectionId) {
    const connectionInfo = this.activeConnections.get(connectionId);
    return connectionInfo && connectionInfo.isActive && connectionInfo.socket.connected;
  }

  /**
   * Get connection info
   */
  getConnectionInfo(connectionId) {
    return this.activeConnections.get(connectionId);
  }
}

// Create singleton instance
const clientSocketManager = new ClientSocketManager();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    clientSocketManager.cleanupAllConnections();
  });

  window.addEventListener('pagehide', () => {
    clientSocketManager.cleanupAllConnections();
  });
}

export { SOCKET_DOMAINS };
export default clientSocketManager;
